import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlayerModalRefType, useClosePlayerModal, useRefreshPlayerModal } from "@/hooks/playerModal";
import { AlertTriangleIcon, HeartPulse as HeartPulseIcon, MailIcon, ShieldCheckIcon } from "lucide-react";
import { KickOneIcon } from '@/components/KickIcons';
import { isCoreDead } from "@shared/utils";
import { useBackendApi } from "@/hooks/fetch";
import { useAdminPerms } from "@/hooks/auth";
import { useOpenPromptDialog } from "@/hooks/dialogs";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import { PlayerModalPlayerData } from "@shared/playerApiTypes";
import type { WarnTemplatesDataType } from "@shared/otherTypes";
import { useLocation, useRoute } from "wouter";
import { useContentRefresh } from "@/hooks/pages";
import { useCloseAllSheets } from "@/hooks/sheets";
import PlayerHealthDialog, { HealPicks } from "./PlayerHealthDialog";
import { useState } from "react";


//a revive fades out, respawns and only then clears the core's dead flag, which
//still has to travel client > server > txadmin before a refetch would see it
const reviveSyncDelay = 3000;

//stacked on mobile as two columns, a centered row from sm up
const footerRowClasses = 'w-full grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-2';

type PlayerModalFooterProps = {
    playerRef: PlayerModalRefType,
    player?: PlayerModalPlayerData,
    warnTemplates?: WarnTemplatesDataType[], //undefined = loading
}

export default function PlayerModalFooter({ playerRef, player, warnTemplates }: PlayerModalFooterProps) {
    const { hasPerm } = useAdminPerms();
    const refreshPlayerModal = useRefreshPlayerModal();
    const [isHealthDialogOpen, setHealthDialogOpen] = useState(false);
    const canHeal = hasPerm('players.heal');
    const canRevive = hasPerm('players.revive');
    const isTargetDead = isCoreDead(player?.coreData);
    const openPromptDialog = useOpenPromptDialog();
    const closeModal = useClosePlayerModal();
    const setLocation = useLocation()[1];
    const [isAlreadyInAdminPage] = useRoute('/admins');
    const refreshContent = useContentRefresh();
    const closeAllSheets = useCloseAllSheets();
    const playerMessageApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/message`,
    });
    const playerKickApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/kick`,
    });
    const playerWarnApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/warn`,
    });
    const playerReviveApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/revive`,
    });
    const playerHealApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/heal`,
    });

    const closeOnSuccess = (data: GenericApiOkResp) => {
        if ('success' in data) {
            closeModal();
            closeAllSheets();
        }
    }

    const handleGiveAdmin = () => {
        if (!player) return;
        const params = new URLSearchParams();
        params.set("autofill", "true");
        params.set("name", player.pureName);
        for (const id of player.idsOnline) {
            if (id.startsWith("discord:")) {
                params.set("discord", id);
            } else if (id.startsWith("fivem:")) {
                params.set("citizenfx", id);
            }
        }
        setLocation(`/admins?${params.toString()}`);
        console.log('isAlreadyInAdminPage', isAlreadyInAdminPage);
        if (isAlreadyInAdminPage) {
            refreshContent();
        }
        closeModal();
        closeAllSheets();
    }

    //the dialog only offers what the admin may do, this is the backstop
    const handleHealthSubmit = (picks: HealPicks) => {
        const wantsRevive = picks.revive && canRevive;
        const wantsHeal = (picks.heal || picks.armour) && canHeal;

        //a revive respawns the ped, so it has to carry the new values instead of
        //racing a second request that would be wiped by it
        if (wantsHeal) {
            const mode = picks.heal && picks.armour
                ? 'full'
                : picks.heal ? 'health' : 'armour';
            playerHealApi({
                queryParams: playerRef,
                data: { mode, revive: wantsRevive },
                genericHandler: { successMsg: wantsRevive ? 'Player revived.' : 'Player healed.' },
                toastLoadingMessage: wantsRevive ? 'Reviving player...' : 'Healing player...',
                success: () => {
                    //the respawn runs on the target's client, so their new state
                    //only reaches us a moment after the request is answered
                    if (wantsRevive) setTimeout(refreshPlayerModal, reviveSyncDelay);
                },
            });
        } else if (wantsRevive) {
            playerReviveApi({
                queryParams: playerRef,
                data: {},
                genericHandler: { successMsg: 'Player revived.' },
                toastLoadingMessage: 'Reviving player...',
                success: () => setTimeout(refreshPlayerModal, reviveSyncDelay),
            });
        }
    }

    const handleDm = () => {
        if (!player) return;
        openPromptDialog({
            title: `Direct Message ${player.displayName}`,
            message: 'Type the direct message below.',
            placeholder: 'The message to send to the player.',
            submitLabel: 'Send',
            required: true,
            onSubmit: (input) => {
                playerMessageApi({
                    queryParams: playerRef,
                    data: { message: input },
                    genericHandler: { successMsg: 'Direct message sent.' },
                    toastLoadingMessage: 'Sending direct message...',
                    success: closeOnSuccess,
                });
            }
        });
    }

    const handleKick = () => {
        if (!player) return;
        openPromptDialog({
            title: `Kick ${player.displayName}`,
            message: 'Type the kick reason below, or leave it blank.',
            placeholder: 'The reason for the kick.',
            submitLabel: 'Send',
            onSubmit: (input) => {
                playerKickApi({
                    queryParams: playerRef,
                    data: { reason: input },
                    genericHandler: { successMsg: 'Player kicked.' },
                    toastLoadingMessage: 'Kicking player...',
                    success: closeOnSuccess,
                });
            }
        });
    }

    const handleWarn = () => {
        if (!player) return;
        openPromptDialog({
            title: `Warn ${player.displayName}`,
            message: <p>
                Type below the warn reason. <br />
                Offline players will receive the warning when they come back online.
            </p>,
            placeholder: 'The reason for the warn, rule violated, etc.',
            submitLabel: 'Send',
            required: true,
            checkbox: {
                label: 'Analyse Warn',
                disabled: !hasPerm('players.analyse'),
                tooltipClass: 'text-analyse-inline',
                disabledTooltip: <p>
                    You need the "Analyse Warns" permission<br />
                    to flag a warn as an analyse warn.
                </p>,
            },
            templates: {
                items: warnTemplates?.map((template) => ({
                    id: template.id,
                    reason: template.reason,
                    flagged: !!template.analyse,
                })),
                addLabel: 'Add Warn Template',
                addPath: '/settings/warn-templates',
                flagBadge: 'AN',
                flagClass: 'text-analyse',
            },
            onSubmit: (input, isAnalyse) => {
                playerWarnApi({
                    queryParams: playerRef,
                    data: { reason: input, analyse: isAnalyse },
                    genericHandler: { successMsg: 'Warning sent.' },
                    toastLoadingMessage: 'Sending warning...',
                    success: closeOnSuccess,
                });
            }
        });
    }

    return (
        <DialogFooter className="max-w-2xl flex flex-col sm:flex-col gap-2 md:gap-4 p-2 md:p-4 border-t sm:justify-center sm:space-x-0">
            {/* the actions come first, the profile links sit below - same split the ingame menu uses */}
            <div className={footerRowClasses}>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={(!canHeal && !canRevive) || !player || !player.isConnected}
                    onClick={() => setHealthDialogOpen(true)}
                    className="pl-2"
                >
                    <HeartPulseIcon className="size-5 mr-1" /> Health
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={!hasPerm('players.direct_message') || !player || !player.isConnected}
                    onClick={handleDm}
                    className="pl-2"
                >
                    <MailIcon className="size-5 mr-1" /> DM
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={!hasPerm('players.warn') || !player}
                    onClick={handleWarn}
                    className="pl-2"
                >
                    <AlertTriangleIcon className="size-5 mr-1" /> Warn
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={!hasPerm('players.kick') || !player || !player.isConnected}
                    onClick={handleKick}
                    className="pl-2"
                >
                    <KickOneIcon style={{
                        height: '1.25rem',
                        width: '1.5rem',
                        marginRight: '0.25rem',
                        fill: 'currentcolor'
                    }} /> Kick
                </Button>
                <Button
                    variant='outline'
                    size='sm'
                    disabled={!hasPerm('manage.admins') || !player || !player.idsOnline.length}
                    onClick={handleGiveAdmin}
                    className="pl-2"
                >
                    <ShieldCheckIcon className="size-5 mr-1" /> Give Admin
                </Button>
            </div>


            <PlayerHealthDialog
                isOpen={isHealthDialogOpen}
                playerName={player?.displayName ?? ''}
                canHeal={canHeal}
                canRevive={canRevive}
                isTargetDead={isTargetDead}
                onOpenChange={setHealthDialogOpen}
                onSubmit={handleHealthSubmit}
            />
        </DialogFooter>
    )
}
